import { CustomLoader } from "@admin/components/ui/custom-loader";
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import {
  DataTable,
  DataTableColumnSettings,
  DataTableProvider,
  type TableColumnDef,
} from "../../components/data-table";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  MapPin,
  Phone,
  Mail,
  ExternalLink,
  PlusCircle,
  X,
  TrendingUp,
  FileText,
  AlertCircle,
} from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import {
  projectsAPI,
  type Project,
  type ProjectMetric,
  type PaginationParams,
  statesAPI,
  districtsAPI,
} from "../../services/api";
import { CommonPagination } from "../../components/CommonPagination";

const STATUS_OPTIONS = [
  { label: "Planned", value: "Planned", color: "bg-amber-50 text-amber-700 border-amber-200" },
  { label: "In Progress", value: "In Progress", color: "bg-sky-50 text-sky-700 border-sky-200" },
  { label: "Completed", value: "Completed", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { label: "Suspended", value: "Suspended", color: "bg-rose-50 text-rose-700 border-rose-200" },
];

interface ProgramMediaItem {
  id: string;
  file?: File;
  url: string;
  isNew: boolean;
  isVideo: boolean;
}

function isVideoFile(file: File | string): boolean {
  if (typeof file === "string") {
    const lower = file.toLowerCase();
    return (
      lower.endsWith(".mp4") ||
      lower.endsWith(".webm") ||
      lower.endsWith(".ogg") ||
      lower.endsWith(".mov")
    );
  }
  return file.type.startsWith("video/");
}

function revokeBlobUrls(items: ProgramMediaItem[]) {
  items.forEach(item => {
    if (item.isNew && item.url.startsWith("blob:")) {
      URL.revokeObjectURL(item.url);
    }
  });
}

function parseProjectMedia(
  project: Project,
  field: "before" | "after" | "banner"
): ProgramMediaItem[] {
  const raw = field === "before"
    ? project.before_media
    : field === "after"
      ? project.after_media
      : project.banner_image;

  if (!raw) return [];

  const urls = Array.isArray(raw)
    ? raw
    : typeof raw === "string"
      ? [raw]
      : [];

  return urls.map((url, index) => ({
    id: `${field}-existing-${index}-${url}`,
    url,
    isNew: false,
    isVideo: isVideoFile(url),
  }));
}

function getMediaUrls(val: string | string[] | null | undefined): string[] {
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
}

// Defined at module level so the object reference is stable — prevents ReactQuill cursor-jump on re-render
const quillModules = {
  toolbar: [
    [{ font: [] }, { size: ["small", false, "large", "huge"] }],
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ["bold", "italic", "underline", "strike", "blockquote", "code-block"],
    [{ color: [] }, { background: [] }],
    [{ script: "sub" }, { script: "super" }],
    [{ list: "ordered" }, { list: "bullet" }, { indent: "-1" }, { indent: "+1" }],
    [{ align: [] }],
    ["link", "image", "video"],
    ["clean"],
  ],
};

const quillFormats = [
  "font", "size",
  "header",
  "bold", "italic", "underline", "strike", "blockquote", "code-block",
  "color", "background",
  "script",
  "list", "bullet", "indent",
  "align",
  "link", "image", "video",
];

export default function ProgramsInitiatives() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Modal / Form state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [viewingProject, setViewingProject] = useState<Project | null>(null);
  const [deletingProject, setDeletingProject] = useState<Project | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Form Fields State
  const [formTitle, setFormTitle] = useState("");
  const [formShortDescription, setFormShortDescription] = useState("");
  const [formLongDescription, setFormLongDescription] = useState("");
  const [descriptionMode, setDescriptionMode] = useState<"text" | "html">("text");
  const [htmlCodeValue, setHtmlCodeValue] = useState("");
  const [formStatus, setFormStatus] = useState("Planned");
  const [formFundsMobilised, setFormFundsMobilised] = useState("0");
  const [formFundsRequired, setFormFundsRequired] = useState("0");
  const [formOutcomes, setFormOutcomes] = useState("");
  const [formAddress1, setFormAddress1] = useState("");
  const [formAddress2, setFormAddress2] = useState("");
  const [formStateVal, setFormStateVal] = useState<string | number>("");
  const [formDistrict, setFormDistrict] = useState<string | number>("");
  const [formCity, setFormCity] = useState<string | number>("");
  const [statesList, setStatesList] = useState<any[]>([]);
  const [districtsList, setDistrictsList] = useState<any[]>([]);
  const [citiesList, setCitiesList] = useState<any[]>([]);
  const [formCountry, setFormCountry] = useState("India");
  const [formPincode, setFormPincode] = useState("");
  const [formMapLink, setFormMapLink] = useState("");
  const [formMobileNo, setFormMobileNo] = useState("");
  const [formEmail, setFormEmail] = useState("");

  // Metrics Dynamic Row State
  const [formMetrics, setFormMetrics] = useState<ProjectMetric[]>([]);

  // Media files uploads (up to 10 files)
  const [beforeMediaItems, setBeforeMediaItems] = useState<ProgramMediaItem[]>([]);
  const [afterMediaItems, setAfterMediaItems] = useState<ProgramMediaItem[]>([]);
  const [bannerMediaItems, setBannerMediaItems] = useState<ProgramMediaItem[]>([]);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params: PaginationParams = {
        page: currentPage,
        page_size: pageSize,
      };
      const res = await projectsAPI.list(params);
      if (res && typeof res === "object") {
        const list = res.results ?? res.data ?? [];
        setProjects(Array.isArray(list) ? list : []);
        const count =
          res.count ??
          res.total_count ??
          (Array.isArray(list) ? list.length : 0);
        setTotalItems(count);
        setTotalPages(Math.ceil(count / pageSize));
      }
    } catch (error) {
      toast.error("Failed to load programs & initiatives");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const loadStates = async () => {
      try {
        const list = await statesAPI.list();
        setStatesList(list);
      } catch (error) {
        console.error("Failed to load states", error);
      }
    };
    loadStates();
  }, []);

  useEffect(() => {
    const loadDistricts = async () => {
      if (!formStateVal) {
        setDistrictsList([]);
        return;
      }
      try {
        const list = await statesAPI.getDistrictsByStateId(formStateVal);
        setDistrictsList(list);
      } catch (error) {
        console.error("Failed to load districts", error);
      }
    };
    loadDistricts();
  }, [formStateVal]);

  useEffect(() => {
    const loadCities = async () => {
      if (!formDistrict) {
        setCitiesList([]);
        return;
      }
      try {
        const list = await districtsAPI.getCitiesByDistrictId(formDistrict);
        setCitiesList(list);
      } catch (error) {
        console.error("Failed to load cities", error);
      }
    };
    loadCities();
  }, [formDistrict]);

  const resetForm = () => {
    setFormTitle("");
    setFormShortDescription("");
    setFormLongDescription("");
    setDescriptionMode("text");
    setHtmlCodeValue("");
    setFormStatus("Planned");
    setFormFundsMobilised("0");
    setFormFundsRequired("0");
    setFormOutcomes("");
    setFormAddress1("");
    setFormAddress2("");
    setFormStateVal("");
    setFormDistrict("");
    setFormCity("");
    setDistrictsList([]);
    setCitiesList([]);
    setFormCountry("India");
    setFormPincode("");
    setFormMapLink("");
    setFormMobileNo("");
    setFormEmail("");
    setFormMetrics([]);
    setBeforeMediaItems(prev => {
      revokeBlobUrls(prev);
      return [];
    });
    setAfterMediaItems(prev => {
      revokeBlobUrls(prev);
      return [];
    });
    setBannerMediaItems(prev => {
      revokeBlobUrls(prev);
      return [];
    });
    setEditingProject(null);
  };

  const handleOpenCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const parseMetrics = (metricsRaw: string | ProjectMetric[] | null | undefined): ProjectMetric[] => {
    if (!metricsRaw) return [];
    if (typeof metricsRaw === "string") {
      try {
        const parsed = JSON.parse(metricsRaw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return Array.isArray(metricsRaw) ? metricsRaw : [];
  };

  const handleOpenEdit = async (project: Project) => {
    setEditingProject(project);
    setFormTitle(project.title ?? "");
    setFormShortDescription(project.short_description ?? "");
    const loadedLongDesc = project.long_description ?? "";
    setFormLongDescription(loadedLongDesc);
    // Always seed the HTML textarea with the raw stored value so switching tabs shows the code
    setHtmlCodeValue(loadedLongDesc);
    // Auto-detect: if the stored content looks like raw HTML markup, open in HTML Code mode
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(loadedLongDesc);
    setDescriptionMode(looksLikeHtml ? "html" : "text");
    setFormStatus(project.status ?? "Planned");
    setFormFundsMobilised(String(project.jbf_funds_mobilised ?? "0"));
    setFormFundsRequired(String(project.funds_required ?? "0"));
    setFormOutcomes(project.outcomes ?? "");
    setFormAddress1(project.address_line_1 ?? "");
    setFormAddress2(project.address_line_2 ?? "");

    // Asynchronously load the districts and cities sequentially
    setFormStateVal(project.state ?? "");
    if (project.state) {
      try {
        const districts = await statesAPI.getDistrictsByStateId(project.state);
        setDistrictsList(districts);
        setFormDistrict(project.district ?? "");

        if (project.district) {
          const cities = await districtsAPI.getCitiesByDistrictId(project.district);
          setCitiesList(cities);
          setFormCity(project.city ?? "");
        } else {
          setFormCity("");
        }
      } catch (err) {
        console.error("Failed to load locations on edit", err);
      }
    } else {
      setFormDistrict("");
      setDistrictsList([]);
      setFormCity("");
      setCitiesList([]);
    }

    setFormCountry(project.country ?? "India");
    setFormPincode(project.pincode ?? "");
    setFormMapLink(project.map_link ?? "");
    setFormMobileNo(project.mobile_no ?? "");
    setFormEmail(project.email ?? "");
    setFormMetrics(parseMetrics(project.project_metrics));

    // Populate media items
    setBeforeMediaItems(parseProjectMedia(project, "before"));
    setAfterMediaItems(parseProjectMedia(project, "after"));
    setBannerMediaItems(parseProjectMedia(project, "banner"));

    setDialogOpen(true);
  };

  const handleFileChange = (
    phase: "before" | "after" | "banner",
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selected = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (!selected.length) return;

    const setItems =
      phase === "before"
        ? setBeforeMediaItems
        : phase === "after"
          ? setAfterMediaItems
          : setBannerMediaItems;

    const currentItems =
      phase === "before"
        ? beforeMediaItems
        : phase === "after"
          ? afterMediaItems
          : bannerMediaItems;

    const remainingSlots = 10 - currentItems.length;
    if (remainingSlots <= 0) {
      toast.error("Maximum 10 media files allowed");
      return;
    }

    const toAdd = selected.slice(0, remainingSlots);
    if (selected.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more files could be added (limit is 10)`);
    }

    const newItems: ProgramMediaItem[] = toAdd.map((file, i) => ({
      id: `${phase}-new-${Date.now()}-${i}-${file.name}`,
      file,
      url: URL.createObjectURL(file),
      isNew: true,
      isVideo: isVideoFile(file),
    }));

    setItems(prev => [...prev, ...newItems]);
  };

  const handleRemoveFileItem = async (
    phase: "before" | "after" | "banner",
    itemId: string
  ) => {
    const setItems =
      phase === "before"
        ? setBeforeMediaItems
        : phase === "after"
          ? setAfterMediaItems
          : setBannerMediaItems;

    const currentItems =
      phase === "before"
        ? beforeMediaItems
        : phase === "after"
          ? afterMediaItems
          : bannerMediaItems;

    const match = currentItems.find(item => item.id === itemId);
    if (!match) return;

    if (!match.isNew && editingProject) {
      try {
        const payload: { before_urls?: string[]; after_urls?: string[]; banner_urls?: string[] } = {};
        if (phase === "before") payload.before_urls = [match.url];
        if (phase === "after") payload.after_urls = [match.url];
        if (phase === "banner") payload.banner_urls = [match.url];

        await projectsAPI.deleteMedia(editingProject.id, payload);
        toast.success("Media deleted successfully from server");


        setItems(prev => prev.filter(item => item.id !== itemId));
      } catch (error: any) {
        toast.error(error?.message ?? "Failed to delete media from server");
      }
    } else {
      if (match.isNew && match.url.startsWith("blob:")) {
        URL.revokeObjectURL(match.url);
      }
      setItems(prev => prev.filter(item => item.id !== itemId));
    }
  };

  const handleAddMetric = () => {
    setFormMetrics([...formMetrics, { project_metric: "", details: "" }]);
  };

  const handleRemoveMetric = (index: number) => {
    const updated = [...formMetrics];
    updated.splice(index, 1);
    setFormMetrics(updated);
  };

  const handleMetricChange = (index: number, key: keyof ProjectMetric, value: string) => {
    const updated = [...formMetrics];
    updated[index][key] = value;
    setFormMetrics(updated);
  };

  const handleSave = async () => {
    if (!formTitle.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formShortDescription.trim()) {
      toast.error("Short description is required");
      return;
    }
    if (!formAddress1.trim() || !formStateVal || !formCity || !formPincode.trim()) {
      toast.error("Primary location details (Address Line 1, State, City, Pincode) are required");
      return;
    }
    if (!formMobileNo.trim() || !formEmail.trim()) {
      toast.error("Contact details (Mobile, Email) are required");
      return;
    }

    // Filter metrics with content
    const filteredMetrics = formMetrics.filter(m => m.project_metric.trim() && m.details.trim());

    setSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", formTitle.trim());
      formData.append("short_description", formShortDescription.trim());
      // Resolve the single active description — only one mode contributes at a time
      const resolvedLongDescription =
        descriptionMode === "html" ? htmlCodeValue.trim() : formLongDescription.trim();
      formData.append("long_description", resolvedLongDescription);
      formData.append("status", formStatus);
      formData.append("jbf_funds_mobilised", formFundsMobilised || "0");
      formData.append("funds_required", formFundsRequired || "0");
      formData.append("outcomes", formOutcomes.trim());
      formData.append("project_metrics", JSON.stringify(filteredMetrics));
      formData.append("address_line_1", formAddress1.trim());
      formData.append("address_line_2", formAddress2.trim());
      formData.append("state", String(formStateVal));
      if (formDistrict) {
        formData.append("district", String(formDistrict));
      }
      formData.append("city", String(formCity));
      formData.append("country", formCountry.trim());
      formData.append("pincode", formPincode.trim());
      formData.append("map_link", formMapLink.trim());
      formData.append("mobile_no", formMobileNo.trim());
      formData.append("email", formEmail.trim());

      // Media files append
      beforeMediaItems.forEach(item => {
        if (item.isNew && item.file) {
          formData.append("before_media", item.file);
        } else if (!item.isNew) {
          formData.append("retained_before_media", item.url);
        }
      });

      afterMediaItems.forEach(item => {
        if (item.isNew && item.file) {
          formData.append("after_media", item.file);
        } else if (!item.isNew) {
          formData.append("retained_after_media", item.url);
        }
      });

      bannerMediaItems.forEach(item => {
        if (item.isNew && item.file) {
          formData.append("banner_image", item.file);
        } else if (!item.isNew) {
          formData.append("retained_banner_image", item.url);
        }
      });

      if (editingProject) {
        await projectsAPI.update(editingProject.id, formData);
        toast.success("Program updated successfully");
      } else {
        await projectsAPI.create(formData);
        toast.success("Program created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchProjects();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to save program");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingProject) return;
    setDeleteLoading(true);
    try {
      await projectsAPI.delete(deletingProject.id);
      toast.success("Program deleted successfully");
      setDeletingProject(null);
      fetchProjects();
    } catch (error: any) {
      toast.error(error?.message ?? "Failed to delete program");
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatCurrency = (val: string | number) => {
    const num = Number(val);
    if (isNaN(num)) return "₹0";
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(num);
  };

  const tableColumns = useMemo<TableColumnDef<Project>[]>(
    () => [
      {
        id: "title",
        label: "Title",
        width: 250,
        renderCell: p => (
          <div className="flex flex-col">
            <span className="font-semibold text-zinc-900 dark:text-zinc-100">{p.title}</span>
            <span className="text-xs text-muted-foreground line-clamp-1">{p.short_description}</span>
          </div>
        ),
      },
      {
        id: "status",
        label: "Status",
        width: 130,
        renderCell: p => {
          const matchingStatus = STATUS_OPTIONS.find(opt => opt.value === p.status);
          const badgeClass = matchingStatus?.color ?? "bg-zinc-100 text-zinc-800 border-zinc-200";
          return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${badgeClass}`}>
              {p.status || "Planned"}
            </span>
          );
        },
      },
      {
        id: "funds",
        label: "Funds Mobilised / Required",
        width: 200,
        renderCell: p => (
          <div className="flex flex-col text-sm">
            <span className="font-medium text-emerald-600">{formatCurrency(p.jbf_funds_mobilised)}</span>
            <span className="text-xs text-muted-foreground border-t mt-0.5 pt-0.5">Required: {formatCurrency(p.funds_required)}</span>
          </div>
        ),
      },
      {
        id: "location",
        label: "Location",
        width: 180,
        renderCell: p => (
          <span className="text-zinc-600 dark:text-zinc-400">
            {p.city_name || p.city}, {p.district_name ? `${p.district_name}, ` : ""}{p.state_name || p.state}
          </span>
        ),
      },
      {
        id: "contact",
        label: "Contact",
        width: 180,
        renderCell: p => (
          <div className="flex flex-col text-xs text-muted-foreground">
            <span>{p.email}</span>
            <span>{p.mobile_no}</span>
          </div>
        ),
      },
      {
        id: "actions",
        label: "Actions",
        width: 140,
        alwaysVisible: true,
        renderCell: p => (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setViewingProject(p)}
              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 border-indigo-100 transition-all duration-200 hover:scale-105"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleOpenEdit(p)}
              className="text-zinc-700 hover:text-zinc-900 border-zinc-200 hover:bg-zinc-50 transition-all duration-200 hover:scale-105"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDeletingProject(p)}
              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-100 transition-all duration-200 hover:scale-105"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-6 p-6 max-w-[1600px] mx-auto animate-in fade-in duration-300">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b pb-5">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Programs & Initiatives
          </h1>
          <p className="text-muted-foreground">
            Manage all programs and initiatives
          </p>
        </div>
        <div>
          <Button onClick={handleOpenCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            Create new program
          </Button>
        </div>
      </div>

      <DataTableProvider tableId="super-admin-projects" columns={tableColumns}>
        <Card className="shadow-sm border-zinc-200 overflow-hidden">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 bg-zinc-50/50 py-4 border-b">
            <CardTitle className="text-lg font-bold text-zinc-800">
              Programs List ({totalItems})
            </CardTitle>
            <DataTableColumnSettings columns={tableColumns} />
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <CustomLoader size="md" />
              </div>
            ) : projects.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                <div className="h-12 w-12 rounded-full bg-zinc-100 flex items-center justify-center mb-3">
                  <FileText className="h-6 w-6 text-zinc-400" />
                </div>
                <h3 className="font-semibold text-zinc-700 text-lg">No programs yet</h3>
                <p className="text-zinc-500 text-sm max-w-sm mt-1">
                  Start by adding your first project to display and manage it here.
                </p>
                <Button onClick={handleOpenCreate} variant="outline" className="mt-4 gap-2">
                  <Plus className="h-4 w-4" /> Create program
                </Button>
              </div>
            ) : (
              <DataTable
                embedded
                hideToolbar
                tableId="super-admin-projects"
                columns={tableColumns}
                data={projects}
                getRowKey={p => p.id}
              />
            )}
          </CardContent>
          {totalItems > 0 && (
            <div className="border-t p-4 bg-zinc-50/50">
              <CommonPagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={setCurrentPage}
                onPageSizeChange={size => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </div>
          )}
        </Card>
      </DataTableProvider>

      {/* View Detail Modal */}
      <Dialog open={!!viewingProject} onOpenChange={open => !open && setViewingProject(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0 rounded-xl gap-0 shadow-lg border-zinc-100 animate-in zoom-in-95 duration-200">
          {viewingProject && (
            <>
              <div className="bg-gradient-to-r from-zinc-900 to-indigo-950 text-white p-6 relative">
                <div className="flex justify-between items-start gap-4 pr-6">
                  <div>
                    <span className="text-indigo-400 font-bold text-xs uppercase tracking-wider">Programs & Initiatives</span>
                    <h2 className="text-2xl sm:text-3xl font-extrabold mt-1">{viewingProject.title}</h2>
                    <div className="flex gap-2 items-center mt-3">
                      <span className={`px-3 py-0.5 rounded-full text-xs font-semibold border ${STATUS_OPTIONS.find(opt => opt.value === viewingProject.status)?.color ?? "bg-zinc-800 text-zinc-300 border-zinc-700"
                        }`}>
                        {viewingProject.status || "Planned"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Description Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="md:col-span-2 space-y-6">
                    <div>
                      <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Short Description</h4>
                      <p className="text-zinc-700 dark:text-zinc-300 text-sm leading-relaxed whitespace-pre-wrap">{viewingProject.short_description}</p>
                    </div>

                    {viewingProject.long_description && (
                      <div>
                        <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Detailed Overview</h4>
                        <div
                          className="text-zinc-700 dark:text-zinc-300 text-sm leading-relaxed prose max-w-none"
                          dangerouslySetInnerHTML={{ __html: viewingProject.long_description }}
                        />
                      </div>
                    )}

                    {viewingProject.outcomes && (
                      <div className="bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/50 rounded-xl p-4">
                        <h4 className="text-xs font-bold text-emerald-800 dark:text-emerald-400 uppercase tracking-wider mb-2">Expected Outcomes</h4>
                        <p className="text-emerald-900 dark:text-emerald-300 text-sm leading-relaxed whitespace-pre-wrap">{viewingProject.outcomes}</p>
                      </div>
                    )}
                  </div>

                  {/* Sidebar stats & Info */}
                  <div className="space-y-6 border-l pl-0 md:pl-6 border-zinc-100">
                    <div>
                      <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3">Funding details</h4>
                      <div className="space-y-4">
                        <div className="bg-zinc-50 p-3 rounded-lg border border-zinc-100">
                          <span className="text-xs text-muted-foreground block">Required Budget</span>
                          <span className="text-xl font-bold text-zinc-900">{formatCurrency(viewingProject.funds_required)}</span>
                        </div>
                        <div className="bg-emerald-50/60 p-3 rounded-lg border border-emerald-100">
                          <span className="text-xs text-emerald-700 block">Funds Mobilised</span>
                          <span className="text-xl font-bold text-emerald-800">{formatCurrency(viewingProject.jbf_funds_mobilised)}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-2">Location & Contacts</h4>
                      <div className="space-y-2.5 text-sm">
                        <div className="flex gap-2 text-zinc-600 dark:text-zinc-400">
                          <MapPin className="h-4 w-4 shrink-0 text-indigo-500 mt-0.5" />
                          <div className="flex flex-col">
                            <span>{viewingProject.address_line_1}</span>
                            {viewingProject.address_line_2 && <span>{viewingProject.address_line_2}</span>}
                            <span>
                              {viewingProject.city_name || viewingProject.city}, {" "}
                              {viewingProject.district_name ? `${viewingProject.district_name}, ` : ""}
                              {viewingProject.state_name || viewingProject.state} - {viewingProject.pincode}
                            </span>
                            <span>{viewingProject.country}</span>
                          </div>
                        </div>

                        {viewingProject.map_link && (
                          <a
                            href={viewingProject.map_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 px-2.5 py-1 rounded-md mt-1 transition-colors"
                          >
                            <ExternalLink className="h-3 w-3" /> View Map link
                          </a>
                        )}

                        <div className="flex gap-2 text-zinc-600 dark:text-zinc-400 items-center pt-2">
                          <Phone className="h-4 w-4 shrink-0 text-indigo-500" />
                          <span>{viewingProject.mobile_no}</span>
                        </div>
                        <div className="flex gap-2 text-zinc-600 dark:text-zinc-400 items-center">
                          <Mail className="h-4 w-4 shrink-0 text-indigo-500" />
                          <span className="truncate">{viewingProject.email}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Metrics */}
                {parseMetrics(viewingProject.project_metrics).length > 0 && (
                  <div className="border-t pt-5">
                    <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                      <TrendingUp className="h-4 w-4 text-indigo-500" /> Impact Metrics
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                      {parseMetrics(viewingProject.project_metrics).map((metric, i) => (
                        <div key={i} className="bg-zinc-50 border border-zinc-100 p-3 rounded-xl flex flex-col text-center">
                          <span className="text-xs font-semibold text-zinc-400 uppercase tracking-tight">{metric.project_metric}</span>
                          <span className="text-lg font-bold text-zinc-800 mt-1">{metric.details}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Media Sections */}
                {(getMediaUrls(viewingProject.banner_image).length > 0 ||
                  getMediaUrls(viewingProject.before_media).length > 0 ||
                  getMediaUrls(viewingProject.after_media).length > 0) && (
                    <div className="space-y-6 border-t pt-5">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-zinc-400">
                        Project Media Files
                      </h4>

                      {/* Banner Media */}
                      {getMediaUrls(viewingProject.banner_image).length > 0 && (
                        <div className="space-y-2">
                          <span className="block text-xs font-semibold uppercase tracking-wider text-zinc-500">
                            Banner Media
                          </span>
                          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                            {getMediaUrls(viewingProject.banner_image).map(
                              (url, i) => (
                                <div
                                  key={i}
                                  className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-zinc-200 bg-zinc-100 shadow-sm"
                                >
                                  {isVideoFile(url) ? (
                                    <video
                                      src={url}
                                      className="h-full w-full object-cover"
                                      controls
                                    />
                                  ) : (
                                    <img
                                      src={url}
                                      alt="Banner"
                                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                  )}
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                        {/* Before State */}
                        {getMediaUrls(viewingProject.before_media).length > 0 && (
                          <div className="space-y-2">
                            <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                              <span className="h-2 w-2 rounded-full bg-amber-500"></span>{" "}
                              Before State
                            </span>
                            <div className="grid grid-cols-2 gap-3">
                              {getMediaUrls(viewingProject.before_media).map(
                                (url, i) => (
                                  <div
                                    key={i}
                                    className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-zinc-200 bg-zinc-100 shadow-sm"
                                  >
                                    {isVideoFile(url) ? (
                                      <video
                                        src={url}
                                        className="h-full w-full object-cover"
                                        controls
                                      />
                                    ) : (
                                      <img
                                        src={url}
                                        alt="Before State"
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                      />
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}

                        {/* After State */}
                        {getMediaUrls(viewingProject.after_media).length > 0 && (
                          <div className="space-y-2">
                            <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-zinc-500">
                              <span className="h-2 w-2 rounded-full bg-emerald-500"></span>{" "}
                              After State
                            </span>
                            <div className="grid grid-cols-2 gap-3">
                              {getMediaUrls(viewingProject.after_media).map(
                                (url, i) => (
                                  <div
                                    key={i}
                                    className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-zinc-200 bg-zinc-100 shadow-sm"
                                  >
                                    {isVideoFile(url) ? (
                                      <video
                                        src={url}
                                        className="h-full w-full object-cover"
                                        controls
                                      />
                                    ) : (
                                      <img
                                        src={url}
                                        alt="After State"
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                      />
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>
              <div className="border-t p-4 flex justify-end bg-zinc-50">
                <Button variant="outline" onClick={() => setViewingProject(null)}>
                  Close view
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl p-0 border-zinc-200 shadow-xl animate-in zoom-in-95 duration-200 flex flex-col gap-0">
          <DialogHeader className="bg-zinc-50 p-6 border-b">
            <DialogTitle className="text-xl font-bold text-zinc-900">
              {editingProject ? "Edit Program Details" : "Create New Program"}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground mt-0.5">
              Fill in the form to register or update the initiative. Required fields are marked with (*).
            </DialogDescription>
          </DialogHeader>

          <div className="p-6 space-y-6 flex-1 overflow-y-auto">
            {/* Basic fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-semibold">Title *</Label>
                <Input
                  id="title"
                  value={formTitle}
                  onChange={e => setFormTitle(e.target.value)}
                  placeholder="e.g. Clean Water Project"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status" className="text-sm font-semibold">Status *</Label>
                <Select value={formStatus} onValueChange={setFormStatus}>
                  <SelectTrigger id="status">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="short_desc" className="text-sm font-semibold">Short Description *</Label>
              <Textarea
                id="short_desc"
                value={formShortDescription}
                onChange={e => setFormShortDescription(e.target.value)}
                placeholder="Brief summary of the program (visible in lists)..."
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="long_desc" className="text-sm font-semibold">
                Detailed Description
              </Label>
              <div className="overflow-hidden rounded-md border">
                <ReactQuill
                  theme="snow"
                  value={formLongDescription}
                  onChange={setFormLongDescription}
                  placeholder="Enter detailed description here..."
                  modules={{
                    toolbar: [
                      [{ header: [1, 2, false] }],
                      ["bold", "italic", "underline", "blockquote"],
                      [{ list: "ordered" }, { list: "bullet" }],
                      ["link", "clean"],
                    ],
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="outcomes" className="text-sm font-semibold">Expected Outcomes</Label>
              <Textarea
                id="outcomes"
                value={formOutcomes}
                onChange={e => setFormOutcomes(e.target.value)}
                placeholder="List direct targets, beneficiaries or expected changes..."
                rows={3}
              />
            </div>

            {/* Financial Details */}
            <div className="border-t pt-5">
              <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">Financials Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="funds_required" className="text-sm font-semibold">Funds Required (INR) *</Label>
                  <Input
                    id="funds_required"
                    type="number"
                    value={formFundsRequired}
                    onChange={e => setFormFundsRequired(e.target.value)}
                    placeholder="e.g. 1000000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="funds_mobilised" className="text-sm font-semibold">JBF Funds Mobilised (INR)</Label>
                  <Input
                    id="funds_mobilised"
                    type="number"
                    value={formFundsMobilised}
                    onChange={e => setFormFundsMobilised(e.target.value)}
                    placeholder="e.g. 500000"
                  />
                </div>
              </div>
            </div>

            {/* Contact Details */}
            <div className="border-t pt-5">
              <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">Contact Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-semibold">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formEmail}
                    onChange={e => setFormEmail(e.target.value)}
                    placeholder="project@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mobile_no" className="text-sm font-semibold">Mobile Number *</Label>
                  <Input
                    id="mobile_no"
                    type="tel"
                    value={formMobileNo}
                    onChange={e => setFormMobileNo(e.target.value)}
                    placeholder="9876543210"
                  />
                </div>
              </div>
            </div>

            {/* Address details */}
            <div className="border-t pt-5">
              <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">Location Details</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="address1" className="text-sm font-semibold">Address Line 1 *</Label>
                    <Input
                      id="address1"
                      value={formAddress1}
                      onChange={e => setFormAddress1(e.target.value)}
                      placeholder="Street name, landmark..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="address2" className="text-sm font-semibold">Address Line 2</Label>
                    <Input
                      id="address2"
                      value={formAddress2}
                      onChange={e => setFormAddress2(e.target.value)}
                      placeholder="Apartment, suite, unit..."
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-sm font-semibold">State *</Label>
                    <Select
                      value={String(formStateVal || "")}
                      onValueChange={(val) => {
                        setFormStateVal(val);
                        setFormDistrict("");
                        setFormCity("");
                        setDistrictsList([]);
                        setCitiesList([]);
                      }}
                    >
                      <SelectTrigger id="state">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {statesList.map(st => (
                          <SelectItem key={st.id} value={String(st.id)}>
                            {st.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="district" className="text-sm font-semibold">District</Label>
                    <Select
                      value={String(formDistrict || "")}
                      onValueChange={(val) => {
                        setFormDistrict(val);
                        setFormCity("");
                        setCitiesList([]);
                      }}
                      disabled={!formStateVal}
                    >
                      <SelectTrigger id="district">
                        <SelectValue placeholder="Select district" />
                      </SelectTrigger>
                      <SelectContent>
                        {districtsList.map(dist => (
                          <SelectItem key={dist.id} value={String(dist.id)}>
                            {dist.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-sm font-semibold">City *</Label>
                    <Select
                      value={String(formCity || "")}
                      onValueChange={setFormCity}
                      disabled={!formDistrict}
                    >
                      <SelectTrigger id="city">
                        <SelectValue placeholder="Select city" />
                      </SelectTrigger>
                      <SelectContent>
                        {citiesList.map(c => (
                          <SelectItem key={c.id} value={String(c.id)}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="pincode" className="text-sm font-semibold">Pincode *</Label>
                    <Input
                      id="pincode"
                      value={formPincode}
                      onChange={e => setFormPincode(e.target.value)}
                      placeholder="302001"
                    />
                  </div>
                  <div className="space-y-2 flex flex-col justify-end">
                    <Label htmlFor="country" className="text-sm font-semibold mb-2">Country</Label>
                    <Input
                      id="country"
                      value={formCountry}
                      onChange={e => setFormCountry(e.target.value)}
                      placeholder="India"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="map_link" className="text-sm font-semibold">Google Maps Link</Label>
                  <Input
                    id="map_link"
                    value={formMapLink}
                    onChange={e => setFormMapLink(e.target.value)}
                    placeholder="https://maps.google.com/..."
                  />
                </div>
              </div>
            </div>

            {/* Impact Metrics rows */}
            <div className="border-t pt-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider">Impact Metrics</h3>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddMetric}
                  className="gap-1 border-indigo-200 text-indigo-700 bg-indigo-50 hover:bg-indigo-100 transition-colors"
                >
                  <PlusCircle className="h-4 w-4" /> Add Metric
                </Button>
              </div>

              {formMetrics.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2 italic">
                  No metrics added yet. Key metrics show quantitative impact (e.g. "Beneficiaries: 500 people").
                </p>
              ) : (
                <div className="space-y-2">
                  {formMetrics.map((metric, i) => (
                    <div key={i} className="flex gap-2 items-center bg-zinc-50 border p-3 rounded-lg animate-in slide-in-from-top-1 duration-150">
                      <div className="grid grid-cols-2 gap-2 flex-1">
                        <div>
                          <Input
                            placeholder="e.g. Beneficiaries"
                            value={metric.project_metric}
                            onChange={e => handleMetricChange(i, "project_metric", e.target.value)}
                          />
                        </div>
                        <div>
                          <Input
                            placeholder="e.g. 500 people"
                            value={metric.details}
                            onChange={e => handleMetricChange(i, "details", e.target.value)}
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => handleRemoveMetric(i)}
                        className="text-rose-500 hover:text-rose-700 hover:bg-rose-50 shrink-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Media Files uploads */}
            <div className="border-t pt-5 space-y-6">
              <h3 className="text-sm font-bold text-zinc-400 uppercase tracking-wider">Project Media Files</h3>

              {/* Banner Image */}
              <div className="space-y-2 border-b pb-6">
                <div className="flex justify-between items-center">
                  <Label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Banner Media (Max 10)</Label>
                  <span className="text-[10px] font-semibold text-zinc-400">{bannerMediaItems.length}/10</span>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 rounded-lg border border-dashed bg-zinc-50/50 p-4 border-zinc-200">
                    <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 text-muted-foreground hover:text-zinc-800 transition-colors">
                      <Plus className="h-4.5 w-4.5 text-zinc-400" />
                      <span className="text-xs font-medium">Select banner media files (images/videos)</span>
                      <input
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        disabled={bannerMediaItems.length >= 10}
                        className="hidden"
                        onChange={e => handleFileChange("banner", e)}
                      />
                    </label>
                  </div>
                  {bannerMediaItems.length > 0 && (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                      {bannerMediaItems.map(item => (
                        <div key={item.id} className="group relative overflow-hidden rounded-xl border bg-zinc-50 shadow-sm aspect-[4/3] flex items-center justify-center">
                          {item.isVideo ? (
                            <video src={item.url} className="h-full w-full object-cover" muted playsInline />
                          ) : (
                            <img src={item.url} alt="Banner Preview" className="h-full w-full object-cover" />
                          )}
                          <button
                            type="button"
                            onClick={() => handleRemoveFileItem("banner", item.id)}
                            className="absolute top-2 right-2 bg-rose-500 text-white rounded-full p-1 hover:bg-rose-600 shadow transition-colors z-10"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Before Media */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Before Media (Max 10)</Label>
                    <span className="text-[10px] font-semibold text-zinc-400">{beforeMediaItems.length}/10</span>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2 rounded-lg border border-dashed bg-zinc-50/50 p-4 border-zinc-200">
                      <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 text-muted-foreground hover:text-zinc-800 transition-colors">
                        <Plus className="h-4.5 w-4.5 text-zinc-400" />
                        <span className="text-xs font-medium">Select before media files</span>
                        <input
                          type="file"
                          accept="image/*,video/*"
                          multiple
                          disabled={beforeMediaItems.length >= 10}
                          className="hidden"
                          onChange={e => handleFileChange("before", e)}
                        />
                      </label>
                    </div>
                    {beforeMediaItems.length > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        {beforeMediaItems.map(item => (
                          <div key={item.id} className="group relative overflow-hidden rounded-xl border bg-zinc-50 shadow-sm aspect-[4/3] flex items-center justify-center">
                            {item.isVideo ? (
                              <video src={item.url} className="h-full w-full object-cover" muted playsInline />
                            ) : (
                              <img src={item.url} alt="Before Preview" className="h-full w-full object-cover" />
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveFileItem("before", item.id)}
                              className="absolute top-2 right-2 bg-rose-500 text-white rounded-full p-1 hover:bg-rose-600 shadow transition-colors z-10"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* After Media */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <Label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">After Media (Max 10)</Label>
                    <span className="text-[10px] font-semibold text-zinc-400">{afterMediaItems.length}/10</span>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-2 rounded-lg border border-dashed bg-zinc-50/50 p-4 border-zinc-200">
                      <label className="flex flex-1 cursor-pointer items-center justify-center gap-2 text-muted-foreground hover:text-zinc-800 transition-colors">
                        <Plus className="h-4.5 w-4.5 text-zinc-400" />
                        <span className="text-xs font-medium">Select after media files</span>
                        <input
                          type="file"
                          accept="image/*,video/*"
                          multiple
                          disabled={afterMediaItems.length >= 10}
                          className="hidden"
                          onChange={e => handleFileChange("after", e)}
                        />
                      </label>
                    </div>
                    {afterMediaItems.length > 0 && (
                      <div className="grid grid-cols-2 gap-3">
                        {afterMediaItems.map(item => (
                          <div key={item.id} className="group relative overflow-hidden rounded-xl border bg-zinc-50 shadow-sm aspect-[4/3] flex items-center justify-center">
                            {item.isVideo ? (
                              <video src={item.url} className="h-full w-full object-cover" muted playsInline />
                            ) : (
                              <img src={item.url} alt="After Preview" className="h-full w-full object-cover" />
                            )}
                            <button
                              type="button"
                              onClick={() => handleRemoveFileItem("after", item.id)}
                              className="absolute top-2 right-2 bg-rose-500 text-white rounded-full p-1 hover:bg-rose-600 shadow transition-colors z-10"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t p-4 flex justify-end gap-2 bg-zinc-50 shrink-0">
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={submitLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={submitLoading}
              className="min-w-[100px]"
            >
              {submitLoading ? (
                <CustomLoader size="xs" />
              ) : editingProject ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={!!deletingProject}
        onOpenChange={open => !open && setDeletingProject(null)}
      >
        <DialogContent className="max-w-md p-6 rounded-xl animate-in zoom-in-95 duration-200">
          <DialogHeader>
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-rose-100 mb-3">
              <AlertCircle className="h-6 w-6 text-rose-600" />
            </div>
            <DialogTitle className="text-center text-lg font-bold text-zinc-900">
              Delete program
            </DialogTitle>
          </DialogHeader>
          <p className="py-2 text-center text-zinc-600 text-sm">
            Are you sure you want to delete &quot;<strong>{deletingProject?.title}</strong>&quot;?
            This action is permanent and cannot be undone.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeletingProject(null)} className="flex-1">
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteLoading}
              className="flex-1 bg-rose-600 hover:bg-rose-700"
            >
              {deleteLoading ? <CustomLoader size="xs" /> : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
